// src/Dashboard/pages/user/Reglements/UsersReglementsDashboard.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useUserReglements } from "./hooks/useUserReglements";

import ReglementsHeader from "./components/ReglementsHeader";
import ReglementsKpis from "./components/ReglementsKpis";
import ReglementsToolbar from "./components/ReglementsToolbar";
import ReglementCard from "./components/ReglementCard";
import ReglementsTable from "./components/ReglementsTable";
import ReglementSkeleton from "./components/ReglementSkeleton";
import ReglementsEmptyState from "./components/ReglementsEmptyState";
import PaymentPlanDrawer from "./components/PaymentPlanDrawer";
import PaymentSuccessBanner from "./components/PaymentSuccessBanner";

export default function UsersReglementsDashboard() {
  useScrollToTop();

  const {
    user,

    reglements,
    filteredReglements,
    visibleReglements,
    seasons,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    maxPayments,
    paymentInterval,
    sessionId,

    payingId,
    notifyingId,
    isAlreadyNotified,

    selectedArticle,
    paymentPlanOpen,
    closePaymentPlan,
    handlePaymentPlanSaved,

    handlePayerClick,
    openPaymentPlan,
    handleNotifyAdmins,

    refresh,
    handleGenerateInvoice,
  } = useUserReglements();

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <ReglementsHeader loading={loading} onRefresh={refresh} />

        {sessionId && (
          <PaymentSuccessBanner sessionId={sessionId} onVerified={refresh} />
        )}

        <ReglementsKpis stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Espace paiement
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Vos commandes & règlements
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Suivez vos licences, cotisations, commandes boutique et
                  échéances. Réglez en ligne de manière sécurisée et prévenez
                  l’administration après confirmation du paiement.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredReglements.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {reglements.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <ReglementsToolbar
                ui={ui}
                seasons={seasons}
                updateUi={updateUi}
                debouncedSearch={debouncedSearch}
                setDebouncedSearch={setDebouncedSearch}
                onReset={resetFilters}
              />
            </div>

            {loading ? (
              <div className="mt-5 space-y-4">
                <div className="hidden lg:block">
                  <ReglementSkeleton variant="table" />
                </div>

                <div className="grid grid-cols-1 gap-4 lg:hidden">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <ReglementSkeleton key={index} />
                  ))}
                </div>
              </div>
            ) : filteredReglements.length === 0 ? (
              <ReglementsEmptyState onReset={resetFilters} />
            ) : (
              <>
                <div className="mt-5 hidden lg:block">
                  <ReglementsTable
                    reglements={visibleReglements}
                    payingId={payingId}
                    notifyingId={notifyingId}
                    isAlreadyNotified={isAlreadyNotified}
                    onPay={handlePayerClick}
                    onInstallments={openPaymentPlan}
                    onNotify={handleNotifyAdmins}
                    onInvoice={handleGenerateInvoice}
                  />
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 lg:hidden">
                  {visibleReglements.map((reglement) => (
                    <ReglementCard
                      key={reglement.id}
                      reglement={reglement}
                      payingId={payingId}
                      notifyingId={notifyingId}
                      isAlreadyNotified={isAlreadyNotified}
                      onPay={handlePayerClick}
                      onInstallments={openPaymentPlan}
                      onNotify={handleNotifyAdmins}
                      onInvoice={handleGenerateInvoice}
                    />
                  ))}
                </div>

                {visibleReglements.length < filteredReglements.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                    >
                      Charger plus
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <PaymentPlanDrawer
        isOpen={paymentPlanOpen}
        onClose={closePaymentPlan}
        totalAmount={selectedArticle?.prix_total}
        maxPayments={maxPayments}
        paymentInterval={paymentInterval}
        article={selectedArticle}
        user={user}
        onSaved={handlePaymentPlanSaved}
      />

      <ToastContainer />
    </section>
  );
}
